import React, { useEffect, useState, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import { Grid, TextField } from '@mui/material';
import { Add } from '@mui/icons-material';
import {
  getAppDisplayProducts,
  getProducts,
  getPointOfSales,
  addSpecialOffer,
  deleteAppDisplayProduct
} from 'actions/productActions';
import { debounce } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import VirtualProductList from './components/VirtualProductsList';
import { arrayToKeys } from 'utils/parsingUtils';
import { TimePicker } from '@mui/x-date-pickers';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const MORNING = 'MORNING';
const AFTERNOON = 'AFTERNOON';
const EVENING = 'EVENING';

const ProductsLoading = () => {
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <Skeleton width={'50%'} height={20} />
      </TableCell>
      <TableCell component="th" scope="row">
        <Skeleton width={'50%'} height={20} />
      </TableCell>
      <TableCell>
        <Skeleton width={'50%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
    </TableRow>
  );
};

const headCells = [
  {
    id: 'product',
    numeric: false,
    disablePadding: true,
    label: 'Product'
  },
  {
    id: 'stock',
    numeric: true,
    disablePadding: false,
    label: 'Image'
  },
  {
    id: 'store',
    numeric: true,
    disablePadding: false,
    label: 'Store'
  },
  {
    id: 'price',
    numeric: true,
    disablePadding: false,
    label: 'Price'
  },
  {
    id: 'display-period-from',
    numeric: true,
    disablePadding: false,
    label: 'Display From'
  },
  {
    id: 'display-period-until',
    numeric: true,
    disablePadding: false,
    label: 'Display Until'
  }
];

function EnhancedTableHead(props) {
  const {
    order,
    orderBy,
  } = props;

  return (
    <TableHead>
      <TableRow>
        <TableCell align={'left'}></TableCell>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={'left'}
            sortDirection={orderBy === headCell.id ? order : false}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const EnhancedTableToolbar = props => {
  return (
    <>
      <Grid
        container
        justify="space-between"
        style={{ padding: '25px 15px 0px 15px' }}>
        <Grid item>
          <h3 style={{ fontSize: 20, fontFamily: 'Arial' }}>
            App Display Products
          </h3>
        </Grid>
        <Grid item>
          <button
            onClick={props.onAddItem}
            style={{
              border: '1px solid #0583FF',
              height: 37.5,
              borderRadius: 18.75,
              alignItems: 'center',
              paddingLeft: 12,
              paddingRight: 12,
              backgroundColor: 'white',
              cursor: 'pointer'
            }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#0583FF'
              }}>
              <div>
                <Add />
              </div>
              <div>
                <span style={{ fontWeight: '600' }}>Add Product</span>
              </div>
            </div>
          </button>
        </Grid>
      </Grid>
    </>
  );
};

const UpdateTableFormHeader = ({ title, onClose }) => {
  return (
    <Grid
      container
      justify="space-between"
      style={{
        padding: 20,
        borderBottom: '1px solid #efefef',
        alignItems: 'center',
        marginBottom: 25
      }}>
      <Grid item>
        <h3 style={{ fontSize: 20, fontFamily: 'Arial' }}>{title}</h3>
      </Grid>
      <Grid item>
        <div style={{ cursor: 'pointer' }} onClick={onClose}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="24" fill="#F7F7FC" />
            <path
              d="M18 18.0002L30.7742 30.7744"
              stroke="#14142B"
              stroke-width="1.93939"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M18 30.7744L30.7742 18.0002"
              stroke="#14142B"
              stroke-width="1.93939"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </Grid>
    </Grid>
  );
};

const AddItem = ({ onClose, inventory, onSelectItem }) => {
  const [productKeys, setProductKeys] = useState([]);
  const [productObjectWithKeys, setProductObjectWithKeys] = useState({});
  const [displayFrom, setDisplayFrom] = useState();
  const [displayTo, setDisplayTo] = useState();
  const [displayPeriod, setDisplayPeriod] = useState();
  const listOfProductsRef = useRef(inventory);

  useEffect(() => {
    const { keys, productsWithKeys } = splitProductsToKeys(inventory);
    setProductKeys(keys);
    setProductObjectWithKeys(productsWithKeys);
  }, []);

  const createDatesWithHours = (from, to) => {
    const toDate = new Date();
    const fromDate = new Date();
    toDate.setHours(to);
    toDate.setMinutes(59);
    fromDate.setHours(from);
    fromDate.setMinutes(0);
    return { toDate, fromDate };
  };

  const onSearch = debounce(searchTerm => {
    const matchingProducts = listOfProductsRef.current.filter(p => {
      if (p.productName) {
        return p.productName.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return false;
    });
    if (matchingProducts) {
      const { keys, productsWithKeys } = splitProductsToKeys(matchingProducts);
      setProductKeys(keys);
      setProductObjectWithKeys(productsWithKeys);
    }
  }, 500);

  const onSelect = item => {
    onSelectItem({ item, displayFrom, displayTo });
  };

  const handleDisplayToChange = val => {
    console.log('to: ', val);
    setDisplayTo(val);
  };

  const handleDisplayFromChange = val => {
    console.log('from: ', val);
    setDisplayFrom(val);
  };

  const onChangeInput = evt => {
    onSearch(evt.target.value);
  };

  const onChangeDisplayPeriod = evt => {
    setDisplayPeriod(evt.target.value);
    if (MORNING === evt.target.value) {
      const { toDate, fromDate } = createDatesWithHours(6, 11);
      setDisplayFrom(fromDate);
      setDisplayTo(toDate);
    } else if (AFTERNOON === evt.target.value) {
      const { toDate, fromDate } = createDatesWithHours(12, 17);
      setDisplayFrom(fromDate);
      setDisplayTo(toDate);
    } else {
      const { toDate, fromDate } = createDatesWithHours(18, 23);
      setDisplayFrom(fromDate);
      setDisplayTo(toDate);
    }
  };

  const splitProductsToKeys = products => {
    const keys = [];
    const productsWithKeys = {};
    products.forEach(p => {
      keys.push(p.productName);
      productsWithKeys[p.productName] = p;
    });
    return { keys, productsWithKeys };
  };

  return (
    <Paper style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 350, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div style={{ position: 'relative', height: '100%' }}>
        <UpdateTableFormHeader
          title={'Add Display Product'}
          onClose={onClose}
        />
        <div style={{ padding: 20 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">
              Display Period
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={displayPeriod}
              label="Display Period"
              onChange={onChangeDisplayPeriod}>
              <MenuItem value={MORNING}>MORNING</MenuItem>
              <MenuItem value={AFTERNOON}>AFTERNOON</MenuItem>
              <MenuItem value={EVENING}>EVENING</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div style={{ padding: 20 }}>
          <TimePicker
            label="Display From"
            value={displayFrom}
            fullWidth
            onChange={handleDisplayFromChange}
            renderInput={params => <TextField {...params} />}
          />
        </div>
        <div style={{ padding: 20 }}>
          <TimePicker
            label="Until"
            value={displayTo}
            fullWidth
            onChange={handleDisplayToChange}
            renderInput={params => <TextField {...params} />}
          />
        </div>
        {displayFrom && displayTo && (
          <>
            <input
              type="textfield"
              placeholder="Search products"
              onChange={onChangeInput}
              style={{
                height: 30,
                marginTop: 15,
                width: '90%',
                marginLeft: 10,
                borderRadius: 15,
                background: '#F7F7FC',
                border: '1px solid #D9DBE9',
                alignItems: 'center',
                padding: '12px 24px 12px 16px'
              }}
            />
            <div>
              <VirtualProductList
                products={productObjectWithKeys}
                productKeys={productKeys}
                onSelectProduct={onSelect}
                rowHeight={100}
                width={330}
                height={550}
              />
            </div>
          </>
        )}
      </div>
    </Paper>
  );
};

const DeleteFieldsButton = ({ numbToEdit, onClick }) => {
  return (
    <div
      style={{
        position: 'absolute',
        backgroundColor: 'red',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        bottom: 50,
        left: '45%',
        width: 276,
        padding: '16px 24px',
        boxShadow: '0px 8px 16px rgba(20, 20, 43, 0.24)',
        height: 56,
        borderRadius: 30,
        color: 'white',
        fontFamily: 'Arial',
        fontWeight: '600',
        cursor: 'pointer'
      }}
      onClick={onClick}>
      <Grid container justify="space-between" style={{ marginTop: 3 }}>
        <Grid item>
          <span>{numbToEdit} product selected</span>
        </Grid>
        <Grid item>
          <span
            style={{
              color: 'white',
              fontFamily: 'Arial',
              textDecoration: 'underline'
            }}>
            Delete
          </span>
        </Grid>
      </Grid>
    </div>
  );
};

export default function EnhancedTable({ cellId, stores }) {
  const [selected, setSelected] = React.useState([]);
  const [specialOffers, setSpecialOffers] = useState([]);
  const [fetchingProducts, setFetchingProducts] = useState(true);
  const [products, setProducts] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [pointOfSales, setPointOfSales] = useState({});

  const productsRef = useRef([]);


  const refreshProducts = async cellId => {
    try {
      setFetchingProducts(true);
      const appDisplayProducts = await getAppDisplayProducts(cellId);
      const products = await getProducts(cellId);
      const stores = await getPointOfSales(cellId);
      const storesKeyVal = arrayToKeys('posName', stores);
      setPointOfSales(storesKeyVal.objectWithKeys);
      const { parsedProductList } = parseProductsToList(products);
      setProducts(parsedProductList);
      setSpecialOffers(appDisplayProducts);
      setFetchingProducts(false);
    } catch (err) {
      setFetchingProducts(false);
    }
  };

  useEffect(() => {
    refreshProducts(cellId);
  }, []);

  const parseProductsToList = products => {
    const parsedProductList = [];
    const bundles = [];
    products.forEach(product => {
      const isBundle = product.platformItemProducts.length > 1;
      if (isBundle) bundles.push(product.platformItemName);
      product.platformItemProducts.forEach(platformItemProduct => {
        const partnerProduct = platformItemProduct.partnerProduct;
        const inStock =
          platformItemProduct.inStock !== null
            ? platformItemProduct.inStock
            : false;
        parsedProductList.push({
          bundleName: isBundle ? product.platformItemName : false,
          storeName: product.pointOfSale.posName,
          inStock,
          price: platformItemProduct.price || 0,
          currency: platformItemProduct.currency,
          platformItemId: platformItemProduct.platformItemId,
          ...partnerProduct
        });
      });
    });
    return { parsedProductList, bundles };
  };

  const onSearch = debounce(searchTerm => {
    const filteredProducts = productsRef.current.filter(p => {
      return p.productName.toLowerCase().includes(searchTerm.toLowerCase());
    });
    setProducts(filteredProducts);
  }, 300);

  const handleClick = (event, offer) => {
    const selectedIndex = selected.findIndex(
      of => of.platformDisplayItemTitle === offer.platformDisplayItemTitle
    );
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, offer);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const onSelectItem = async ({ item, displayFrom, displayTo }) => {
    try {
      const platformDisplayItem = {
        platformDisplayId: cellId,
        platformDisplayItemTypeId: 2,
        platformDisplayItemTitle: item.productName,
        platformDisplayItemSubTitle: item.storeName,
        platformDisplayItemBody: item.price,
        platformDisplayItemImage: item.image,
        platformItemId: item.platformItemId,
        displayFrom,
        displayUntil: displayTo,
        posId: pointOfSales[item.storeName].id
      };
      await addSpecialOffer(platformDisplayItem);
      setShowAddItem(false);
      refreshProducts(cellId);
    } catch (err) {
      console.log(err);
    }
  };

  const onDeleteItem = () => {
    const deleteReqs = selected.map(item => {
      return deleteAppDisplayProduct(item.id);
    });
    Promise.all(deleteReqs)
      .then(res => {
        setSelected([]);
        refreshProducts(cellId);
        setShowAddItem(false);
      })
      .catch(err => {
        setSelected([]);
        setShowAddItem(false);
      });
  };

  const isSelected = name => {
    return (
      selected.findIndex(offer => offer.platformDisplayItemTitle === name) !==
      -1
    );
  };

  return (
    <div sx={{ width: '100%' }}>
      <Paper
        sx={{ mb: 2 }}
        style={{ position: 'relative', top: 0, left: 0, width: '100%' }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          onAddItem={() => {
            setShowAddItem(true);
          }}
          onSearch={onSearch}
        />
        <TableContainer
          style={{
            height: Math.round(window.innerHeight * 0.75),
            overflow: 'auto'
          }}>
          <Table sx={{ minWidth: 750 }} size="small">
            <EnhancedTableHead />
            <TableBody style={{ height: 500, overflowY: 'auto' }}>
              {fetchingProducts && (
                <>
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                </>
              )}
              {!fetchingProducts &&
                specialOffers.map((row, index) => {
                  const isItemSelected = isSelected(
                    row.platformDisplayItemTitle
                  );

                  const fromTime = new Date(
                    row.displayFrom
                  ).toLocaleTimeString();
                  const untilTime = new Date(
                    row.displayUntil
                  ).toLocaleTimeString();

                  return (
                    <TableRow
                      hover
                      onClick={event => handleClick(event, row)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={`${row.productName}_${index}`}
                      selected={isItemSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox color="primary" checked={isItemSelected} />
                      </TableCell>
                      <TableCell component="th" scope="row" padding="none">
                        {row.platformDisplayItemTitle}
                      </TableCell>
                      <TableCell>
                        <img
                          alt="special offer"
                          src={row.platformDisplayItemImage}
                          style={{ height: 80, width: 150 }}></img>
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {row.pointOfSale.posName}
                      </TableCell>
                      <TableCell>{row.platformDisplayItemBody}</TableCell>
                      <TableCell>{fromTime}</TableCell>
                      <TableCell>{untilTime}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        {selected.length > 0 && (
          <DeleteFieldsButton
            numbToEdit={selected.length}
            onClick={() => {
              onDeleteItem();
            }}
          />
        )}
        {showAddItem && (
          <AddItem
            inventory={products}
            onClose={() => {
              setShowAddItem(false);
            }}
            onSelectItem={onSelectItem}
          />
        )}
      </Paper>
    </div>
  );
}
